import { Box3, Frustum, Group, InstancedBufferGeometry, Matrix4, Mesh, Vector2, Vector3 } from 'three';
import type { Camera, Object3D, ShaderMaterial, Texture } from 'three';
import type { MapDef, QualityPreset } from '@cloudpearl/config';
import { FLOWER_RING, GRASS_MAX_TILES, GRASS_PACK, bladeGeometry, createFlowerMaterial, createGrassMaterial, flowerGeometry, flowerInstances, grassTileLayout } from '@cloudpearl/assets';
import type { GrassRing, SharedUniforms } from '@cloudpearl/assets';

interface RingDraw {
  ring: GrassRing;
  mesh: Mesh;
  geometry: InstancedBufferGeometry;
  tiles: Float32Array;
  perTile: number;
  spacing: number;
  ground: Vector2;
  blades: { value: number };
}

/** Height range of the streamed-in solid ground over a rectangle (false: none there). */
export type GroundRange = (x0: number, z0: number, x1: number, z1: number, out: { min: number; max: number }) => boolean;

const BAND = 5;
const FLOWER_OUTER = FLOWER_RING.outer;
const _frustum = new Frustum();
const _m = new Matrix4();
const _box = new Box3();
const _min = new Vector3();
const _max = new Vector3();
const _range = { min: 0, max: 0 };

/**
 * GPU grass, Ghost-of-Tsushima style: blades are generated in the vertex shader from
 * gl_InstanceID on a world-anchored jittered grid, ground height from the local heightfield.
 * Each LOD ring is drawn as a list of world tiles; every frame the CPU keeps only the tiles that
 * overlap the ring and the view frustum, so no vertex work is spent behind the camera or in the
 * ring's hole — which is what lets the near rings be very dense.
 */
export class Grass {
  private readonly rings: RingDraw[] = [];
  private readonly meshes: Mesh[] = [];
  private readonly materials: ShaderMaterial[] = [];
  readonly outer: number;
  /** All grass/flower meshes (hidden as one, e.g. for reflection probes). */
  readonly group = new Group();

  constructor(parent: Object3D, shared: SharedUniforms, map: MapDef, quality: QualityPreset, heightfield: Texture) {
    const densityScale = Math.sqrt(48 / Math.max(4, map.grass.density));
    // Densify the near rings; the outermost ring's blades are sub-pixel, so it keeps its spacing.
    const rings: GrassRing[] = quality.grassRings.map((r, i, all) => ({ ...r, spacing: r.spacing * (i === all.length - 1 && all.length > 1 ? Math.max(densityScale, 1) : densityScale) }));
    this.outer = rings.length ? rings[rings.length - 1]!.outer : 0;
    parent.add(this.group);
    if (!map.grass.enabled) return;
    for (const [ri, ring] of rings.entries()) {
      const blade = bladeGeometry(ring.segments);
      const g = new InstancedBufferGeometry();
      g.index = blade.index;
      g.setAttribute('position', blade.getAttribute('position'));
      g.instanceCount = 0;
      const mat = createGrassMaterial(shared, map, heightfield, ring, ri === rings.length - 1);
      const m = new Mesh(g, mat);
      m.frustumCulled = false;
      m.receiveShadow = true;
      // Grass draws before the terrain, nearest ring first and nearest tiles first: hidden blades
      // and the ground beneath them are rejected by the depth test before any shading.
      m.renderOrder = -20 + ri;
      this.group.add(m);
      this.meshes.push(m);
      this.materials.push(mat);
      const layout = grassTileLayout(ring);
      this.rings.push({
        ring,
        mesh: m,
        geometry: g,
        tiles: mat.uniforms['uTiles']!.value as Float32Array,
        perTile: layout.n * layout.n,
        spacing: layout.spacing,
        ground: mat.uniforms['uGround']!.value as Vector2,
        blades: mat.uniforms['uBlades'] as { value: number },
      });
      const side = Math.ceil((2 * ring.outer) / ring.tile) + 2;
      this.cap = Math.max(this.cap, side * side);
    }
    if (quality.flowers && map.grass.flowers.length && map.grass.flowerRatio > 0) {
      const fg = flowerGeometry();
      const g = new InstancedBufferGeometry();
      g.index = fg.index;
      g.setAttribute('position', fg.getAttribute('position'));
      g.setAttribute('uv', fg.getAttribute('uv'));
      g.instanceCount = flowerInstances();
      const mat = createFlowerMaterial(shared, map, heightfield);
      const m = new Mesh(g, mat);
      m.frustumCulled = false;
      m.receiveShadow = true;
      this.group.add(m);
      this.meshes.push(m);
      this.materials.push(mat);
      this.flowerGround = mat.uniforms['uGround']!.value as Vector2;
    }
  }

  private flowerGround: Vector2 | null = null;
  /** Per-frame tile picks (preallocated: no garbage). Sort keys pack distance and pick index. */
  private cap = 0;
  private pickX = new Float64Array(0);
  private pickZ = new Float64Array(0);
  private keys = new Float64Array(0);

  /**
   * Pick each ring's visible tiles for this camera (call once per frame before rendering): tiles
   * are boxed by the real ground height under them, so culling holds at any camera height and
   * pitch, and tiles with no solid ground (open sky between islands) are skipped outright.
   */
  update(camera: Camera, groundRange: GroundRange): void {
    if (!this.rings.length) return;
    camera.updateMatrixWorld();
    _m.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    _frustum.setFromProjectionMatrix(_m);
    const cx = camera.position.x;
    const cz = camera.position.z;
    if (this.pickX.length < this.cap) {
      this.pickX = new Float64Array(this.cap);
      this.pickZ = new Float64Array(this.cap);
      this.keys = new Float64Array(this.cap);
    }
    const px = this.pickX;
    const pz = this.pickZ;
    const keys = this.keys;
    let fMin = Infinity;
    let fMax = -Infinity;
    for (const r of this.rings) {
      let gMin = Infinity;
      let gMax = -Infinity;
      const T = r.ring.tile;
      const inner = r.ring.inner > 0 ? r.ring.inner - BAND : -1;
      const outer = r.ring.outer;
      const i0 = Math.floor((cx - outer) / T);
      const i1 = Math.floor((cx + outer) / T);
      const j0 = Math.floor((cz - outer) / T);
      const j1 = Math.floor((cz + outer) / T);
      let n = 0;
      for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
          const x0 = i * T;
          const z0 = j * T;
          // Chebyshev distance range of the tile from the camera (rings are square annuli).
          const dxMin = Math.max(x0 - cx, cx - (x0 + T), 0);
          const dzMin = Math.max(z0 - cz, cz - (z0 + T), 0);
          const near = Math.max(dxMin, dzMin);
          const far = Math.max(Math.abs(x0 - cx), Math.abs(x0 + T - cx), Math.abs(z0 - cz), Math.abs(z0 + T - cz));
          if (near > outer || far < inner) continue;
          if (!groundRange(x0, z0, x0 + T, z0 + T, _range)) continue;
          _box.set(_min.set(x0, _range.min - 0.5, z0), _max.set(x0 + T, _range.max + 1.5, z0 + T));
          if (!_frustum.intersectsBox(_box)) continue;
          px[n] = x0;
          pz[n] = z0;
          // Nearest first (front-to-back for early depth rejection); index in the low bits.
          keys[n] = Math.floor(near * 64) * 4096 + n;
          n++;
          if (_range.min < gMin) gMin = _range.min;
          if (_range.max > gMax) gMax = _range.max;
          if (near < FLOWER_OUTER) {
            if (_range.min < fMin) fMin = _range.min;
            if (_range.max > fMax) fMax = _range.max;
          }
        }
      }
      r.ground.set(gMin, gMax);
      const sorted = keys.subarray(0, n).sort();
      const count = Math.min(n, GRASS_MAX_TILES);
      const inv = 1 / r.spacing;
      for (let k = 0; k < count; k++) {
        const pi = sorted[k]! % 4096;
        r.tiles[k * 2] = Math.round(px[pi]! * inv);
        r.tiles[k * 2 + 1] = Math.round(pz[pi]! * inv);
      }
      const blades = count * r.perTile;
      r.blades.value = blades;
      r.geometry.instanceCount = Math.ceil(blades / GRASS_PACK);
      r.mesh.visible = count > 0;
    }
    if (this.flowerGround) {
      this.flowerGround.set(fMin, fMax);
      this.meshes[this.meshes.length - 1]!.visible = fMin <= fMax;
    }
  }

  get allMaterials(): ShaderMaterial[] {
    return this.materials;
  }

  dispose(): void {
    for (const m of this.meshes) {
      m.removeFromParent();
      m.geometry.dispose();
    }
    this.group.removeFromParent();
    for (const m of this.materials) m.dispose();
  }
}
